/**
 * Viko Call (docs/ux/voice-call.md, docs/research/viko-call-architecture.md §4 and §6).
 *
 * - `CallBrief`: the compact portfolio state the call agent reads at call start (≤6k tokens),
 *   then `CallBriefDelta`s between turns. Built on the Mac from decrypted state; it only leaves
 *   the Mac sealed.
 * - `CallFrame`: daemon → device frames of one call (captions, spoken chunks, `call.show`, the
 *   queue, the secure batch, whisper transcripts). They travel as sealed `call.frame` events on
 *   the call's encrypted stream, coalesced like any run's events.
 * - Commands (device → daemon, device-signed): `call.start`, `call.utterance`, `call.audio`,
 *   `call.end`. None is risky: the call can only *queue* a risky action; executing one takes the
 *   existing passkey step-up (`land.merge`, `land.deploy`, `decision.answer.risky`, …).
 * - MCP tools for the call session: `call_show`, `call_queue_secure`, `call_notify_when`,
 *   `feedback_capture`, `call_act`.
 *
 * Additive only: no existing shape changes.
 */
import { z } from 'zod';
/**
 * Same as `PrivateEntityId` (encrypted-entities.ts). Defined here because events.ts imports this
 * module and encrypted-entities.ts → content.ts → events.ts would be an import cycle.
 */
const PrivateEntityId = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
const At = z.number().int().nonnegative();
const LocalId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
/** Project ids are opaque entity ids on encrypted accounts and short ids on legacy ones. */
const ProjectId = z.string().min(1).max(256);
const CallId = PrivateEntityId;
/** The call a command was sent from (an optional field on the commands a call can send). */
export const CallVia = PrivateEntityId;
// ---------------------------------------------------------------------------------------------
// Portfolio brief
// ---------------------------------------------------------------------------------------------
/** Token budget for the whole brief (≈4 chars per token). */
export const CALL_BRIEF_TOKEN_BUDGET = 6000;
export const CALL_BRIEF_PROJECTS_MAX = 12;
export const CALL_BRIEF_BLOCKERS_MAX = 3;
export const CALL_BRIEF_DECISIONS_MAX = 5;
export const CALL_BRIEF_RUNNING_MAX = 5;
export const CALL_BRIEF_BUILDS_MAX = 5;
export const CALL_BRIEF_FEEDBACK_MAX = 3;
export const CALL_BRIEF_VOCAB_MAX = 40;
/** Estimated tokens of a JSON value (the budget check; conservative: 1 token ≈ 4 chars). */
export function estimateTokens(value) {
    return Math.ceil(JSON.stringify(value).length / 4);
}
/**
 * One option of a decision, verbatim from the decision record. `n` is the spoken number
 * (1-based, the record's stored order). The agent never rewrites labels: the device reads and
 * renders these exact strings, and "two" always means `n === 2`.
 */
export const CallOption = z.strictObject({
    n: z.number().int().min(1).max(9),
    id: LocalId,
    label: z.string().min(1).max(200),
    suggested: z.boolean(),
});
export const CallDecision = z.strictObject({
    id: PrivateEntityId,
    projectId: ProjectId,
    title: z.string().max(120),
    question: z.string().min(1).max(600),
    options: z.array(CallOption).max(9),
    /** Answering needs the passkey batch (risky category). */
    secure: z.boolean(),
    blocking: z.boolean(),
    unblocks: z.number().int().nonnegative().max(1000),
    createdAt: At,
});
export const CallBlocker = z.strictObject({
    rank: z.number().int().min(1).max(CALL_BRIEF_BLOCKERS_MAX),
    title: z.string().min(1).max(160),
    reason: z.string().max(240),
    runId: z.string().max(256).optional(),
});
export const CallRunning = z.strictObject({
    runId: z.string().min(1).max(256),
    title: z.string().max(160),
    status: z.enum(['running', 'waiting', 'failed']),
    machine: z.string().max(80).optional(),
    agent: z.string().max(40).optional(),
    /** "4 of 6 steps", "checks". */
    step: z.string().max(80).optional(),
});
export const CallBuild = z.strictObject({
    lane: z.string().min(1).max(80),
    label: z.string().max(160),
    status: z.enum(['building', 'ready', 'live', 'failed']),
    version: z.string().max(80).optional(),
    at: At,
});
/** Feedback is untrusted user-facing data: never instructions (see `wrapUntrusted`). */
export const CallFeedback = z.strictObject({
    id: z.string().min(1).max(256),
    source: z.enum(['speech', 'screenshot', 'share', 'testflight', 'app']),
    summary: z.string().max(400),
    at: At,
});
export const CallBriefProject = z.strictObject({
    id: ProjectId,
    name: z.string().min(1).max(120),
    goal: z.string().max(400).optional(),
    /** Needs-you count for the project (the same selector as every badge). */
    needs: z.number().int().nonnegative(),
    blockers: z.array(CallBlocker).max(CALL_BRIEF_BLOCKERS_MAX),
    decisions: z.array(CallDecision).max(CALL_BRIEF_DECISIONS_MAX),
    running: z.array(CallRunning).max(CALL_BRIEF_RUNNING_MAX),
    builds: z.array(CallBuild).max(CALL_BRIEF_BUILDS_MAX),
    feedback: z.array(CallFeedback).max(CALL_BRIEF_FEEDBACK_MAX),
    /** Items dropped to fit the budget (the agent can fetch more with Viko tools). */
    more: z
        .strictObject({
        decisions: z.number().int().nonnegative(),
        running: z.number().int().nonnegative(),
        builds: z.number().int().nonnegative(),
        feedback: z.number().int().nonnegative(),
    })
        .optional(),
});
export const CALL_BRIEF_SOURCES = ['runs', 'decisions', 'builds', 'feedback', 'goals'];
export const CallBriefSource = z.enum(CALL_BRIEF_SOURCES);
export const CallBrief = z.strictObject({
    v: z.literal(1),
    seq: z.number().int().nonnegative(),
    generatedAt: At,
    /** When each source was last refreshed (null: never loaded on this machine). */
    freshness: z.record(CallBriefSource, At.nullable()),
    /** Total needs-you across all projects (matches the Home count). */
    needsTotal: z.number().int().nonnegative(),
    /** Ranked: most needs-you first. Truncated by rank to fit the budget. */
    projects: z.array(CallBriefProject).max(CALL_BRIEF_PROJECTS_MAX),
    /** Projects dropped by the 12-project cap. */
    moreProjects: z.number().int().nonnegative(),
    /** Names for speech recognition (whisper prompt, transcript normalisation). */
    vocabulary: z.array(z.string().min(1).max(60)).max(CALL_BRIEF_VOCAB_MAX),
});
/** Paths a delta may touch. Project-level ops replace or remove one project or one field. */
export const CALL_BRIEF_PROJECT_FIELDS = [
    'name',
    'goal',
    'needs',
    'blockers',
    'decisions',
    'running',
    'builds',
    'feedback',
    'more',
];
export const CallBriefDeltaOp = z.discriminatedUnion('op', [
    z.strictObject({
        op: z.literal('set'),
        path: z.union([
            z.tuple([z.literal('projects'), ProjectId]),
            z.tuple([z.literal('projects'), ProjectId, z.enum(CALL_BRIEF_PROJECT_FIELDS)]),
            z.tuple([z.literal('needsTotal')]),
            z.tuple([z.literal('moreProjects')]),
            z.tuple([z.literal('vocabulary')]),
            z.tuple([z.literal('freshness'), CallBriefSource]),
            z.tuple([z.literal('order')]),
        ]),
        value: z.unknown(),
    }),
    z.strictObject({
        op: z.literal('remove'),
        path: z.union([
            z.tuple([z.literal('projects'), ProjectId]),
            z.tuple([z.literal('projects'), ProjectId, z.enum(CALL_BRIEF_PROJECT_FIELDS)]),
        ]),
    }),
]);
export const CallBriefDelta = z.strictObject({
    seq: z.number().int().positive(),
    baseSeq: z.number().int().nonnegative(),
    generatedAt: At,
    ops: z.array(CallBriefDeltaOp).min(1).max(256),
});
/** Apply a delta; returns undefined on a `seq` gap (the caller asks for a full brief). */
export function applyCallBriefDelta(brief, d) {
    if (d.baseSeq !== brief.seq)
        return undefined;
    const projects = new Map(brief.projects.map((p) => [p.id, p]));
    let order = brief.projects.map((p) => p.id);
    const next = { ...brief, seq: d.seq, generatedAt: d.generatedAt };
    for (const op of d.ops) {
        const [head, id, field] = op.path;
        if (head === 'projects' && id) {
            if (op.op === 'remove') {
                if (field) {
                    const p = projects.get(id);
                    if (p) {
                        const { [field]: _drop, ...rest } = p;
                        projects.set(id, rest);
                    }
                }
                else {
                    projects.delete(id);
                    order = order.filter((x) => x !== id);
                }
            }
            else if (field) {
                const p = projects.get(id);
                if (p)
                    projects.set(id, { ...p, [field]: op.value });
            }
            else {
                if (!projects.has(id))
                    order.push(id);
                projects.set(id, op.value);
            }
        }
        else if (op.op === 'set') {
            if (head === 'order')
                order = op.value.filter((x) => projects.has(x));
            else if (head === 'freshness' && id)
                next.freshness = { ...next.freshness, [id]: op.value };
            else if (head === 'needsTotal')
                next.needsTotal = op.value;
            else if (head === 'moreProjects')
                next.moreProjects = op.value;
            else if (head === 'vocabulary')
                next.vocabulary = op.value;
        }
    }
    next.projects = order.map((x) => projects.get(x)).filter((p) => !!p);
    return CallBrief.parse(next);
}
// ---------------------------------------------------------------------------------------------
// Queue items, secure batch, show targets
// ---------------------------------------------------------------------------------------------
/** One item in the call's burn-down queue (ranked; `decision` items read verbatim options). */
export const CallQueueItem = z.strictObject({
    key: LocalId,
    kind: z.enum(['decision', 'blocker', 'build', 'feedback']),
    projectId: ProjectId,
    projectName: z.string().max(120),
    title: z.string().max(200),
    decision: CallDecision.optional(),
    state: z.enum(['pending', 'current', 'answered', 'skipped', 'queued-secure']),
});
/** Command kinds the call may queue for the passkey batch (risky kinds only). */
export const CallSecureKind = z.enum(['land.merge', 'land.deploy', 'decision.answer.risky']);
/**
 * A queued secure action. `payload` is exactly what the device will sign (validated against the
 * command's own schema before signing). `production` items are offered for the passkey at once;
 * the rest wait for the end-of-call batch.
 */
export const CallSecureItem = z.strictObject({
    id: LocalId,
    kind: CallSecureKind,
    projectId: ProjectId,
    title: z.string().min(1).max(200),
    detail: z.string().max(400).optional(),
    payload: z.record(z.string(), z.unknown()),
    production: z.boolean(),
    queuedAt: At,
    state: z.enum(['queued', 'approved', 'removed', 'failed']),
});
export const CallShowTarget = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('decision'), decisionId: PrivateEntityId }),
    z.strictObject({ type: z.literal('artifact'), artifactId: z.string().min(1).max(256) }),
    z.strictObject({
        type: z.literal('artboards'),
        decisionId: PrivateEntityId,
        title: z.string().max(200).optional(),
    }),
    z.strictObject({
        type: z.literal('build'),
        projectId: ProjectId,
        lane: z.string().min(1).max(80),
    }),
    z.strictObject({ type: z.literal('diff'), workstreamId: z.string().min(1).max(256) }),
    z.strictObject({ type: z.literal('run'), runId: z.string().min(1).max(256) }),
    z.strictObject({ type: z.literal('secure-batch') }),
]);
/** Where the user barged in: the agent must not assume the unheard part was heard. */
export const CallInterruption = z.strictObject({
    /** Agent text spoken in full before the barge-in. */
    heard: z.string().max(4000),
    /** The sentence in progress, up to the last word heard. */
    cutAt: z.string().max(1000),
    /** Text queued or streamed but never spoken. */
    unheard: z.string().max(8000),
    /** The agent turn that was cut. */
    turnId: LocalId.optional(),
});
// ---------------------------------------------------------------------------------------------
// Frames (daemon → device), sealed as `call.frame` events on the call's stream
// ---------------------------------------------------------------------------------------------
export const CallMode = z.enum(['local', 'live']);
/** How the device hears the user in Local mode. Never a browser vendor's cloud recognizer. */
export const CallSpeechPath = z.enum(['browser-local', 'mac-whisper', 'typed']);
export const CallFrame = z.discriminatedUnion('t', [
    z.strictObject({
        t: z.literal('ready'),
        callId: CallId,
        agent: z.string().max(40),
        whisper: z.enum(['ready', 'fetching', 'unavailable']),
    }),
    z.strictObject({ t: z.literal('brief'), brief: CallBrief }),
    z.strictObject({ t: z.literal('brief.delta'), delta: CallBriefDelta }),
    z.strictObject({ t: z.literal('queue'), items: z.array(CallQueueItem).max(64) }),
    /** A spoken chunk (clause or sentence) of an agent turn, in order. */
    z.strictObject({
        t: z.literal('say'),
        turnId: LocalId,
        idx: z.number().int().nonnegative(),
        text: z.string().max(2000),
        final: z.boolean(),
    }),
    z.strictObject({
        t: z.literal('state'),
        state: z.enum(['thinking', 'idle', 'ended', 'error']),
        note: z.string().max(400).optional(),
    }),
    z.strictObject({ t: z.literal('show'), target: CallShowTarget }),
    z.strictObject({ t: z.literal('secure'), items: z.array(CallSecureItem).max(32) }),
    /** A voice-tier action the device signs (never a risky kind). */
    z.strictObject({
        t: z.literal('act'),
        id: LocalId,
        action: z.lazy(() => CallVoiceAction),
    }),
    z.strictObject({
        t: z.literal('transcript'),
        utteranceId: LocalId,
        text: z.string().max(4000),
        ms: z.number().int().nonnegative().optional(),
    }),
    z.strictObject({
        t: z.literal('notify'),
        id: LocalId,
        text: z.string().max(400),
        target: CallShowTarget.optional(),
    }),
    z.strictObject({
        t: z.literal('filed'),
        feedbackId: z.string().max(256),
        projectId: ProjectId,
        summary: z.string().max(400),
    }),
]);
/**
 * Voice-tier actions: reversible or internal only. The device validates each against the
 * decision record it holds and signs the matching non-risky command itself.
 */
export const CallVoiceAction = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('decision.pick'),
        decisionId: PrivateEntityId,
        projectId: ProjectId,
        /** Spoken number: the stored option at this position (1-based). */
        n: z.number().int().min(1).max(9),
        note: z.string().max(4000).optional(),
    }),
    z.strictObject({
        kind: z.literal('decision.note'),
        decisionId: PrivateEntityId,
        projectId: ProjectId,
        note: z.string().min(1).max(4000),
    }),
    z.strictObject({
        kind: z.literal('run.message'),
        runId: z.string().min(1).max(256),
        text: z.string().min(1).max(4000),
    }),
    z.strictObject({ kind: z.literal('run.stop'), runId: z.string().min(1).max(256) }),
    z.strictObject({
        kind: z.literal('ship.preview'),
        projectId: ProjectId,
        /** Non-production lanes only (staging, web preview, TestFlight). */
        lane: z.string().min(1).max(80),
    }),
]);
/** Lanes that are production (secure batch, offered at once). Anything else is voice tier. */
export function isProductionLane(lane) {
    return /^(prod|production|release|app-?store|public)(\b|[-_:])/i.test(lane) || lane === 'main';
}
// ---------------------------------------------------------------------------------------------
// Call record (entity kind `call`): what happened on a call, for History ("via call")
// ---------------------------------------------------------------------------------------------
export const CALL_RECORD_ACTIONS_MAX = 500;
export const CallAction = z.strictObject({
    /** The signed command's id, or the secure item id while it waits for the passkey. */
    id: z.string().min(1).max(256),
    at: At,
    kind: z.string().min(1).max(64),
    title: z.string().max(200),
    projectId: ProjectId.optional(),
    tier: z.enum(['voice', 'secure']),
    state: z.enum(['done', 'failed', 'queued', 'removed']),
});
export const CallRecord = z.strictObject({
    v: z.literal(1),
    /** The call's own encrypted stream (captions, frames). */
    runId: z.string().min(1).max(256),
    machineId: z.string().max(256).optional(),
    mode: CallMode,
    speech: CallSpeechPath,
    agent: z.string().max(40),
    startedAt: At,
    endedAt: At.optional(),
    utterances: z.number().int().nonnegative(),
    actions: z.array(CallAction).max(CALL_RECORD_ACTIONS_MAX),
    /** Projects the call touched (History lists the call under each). */
    projectIds: z.array(ProjectId).max(64),
});
// ---------------------------------------------------------------------------------------------
// Commands (device → daemon); spread into COMMAND_PAYLOADS
// ---------------------------------------------------------------------------------------------
export const CALL_AUDIO_SAMPLE_RATE = 16_000;
/**
 * One `call.audio` command carries at most 3.5 s of 16 kHz mono Int16 (112 KB raw): sealed and
 * base64'd twice it stays under the hub's 256 KB command cap.
 */
export const CALL_AUDIO_FRAME_MAX_MS = 3500;
export const CALL_AUDIO_FRAME_MAX_BYTES = (CALL_AUDIO_SAMPLE_RATE * 2 * CALL_AUDIO_FRAME_MAX_MS) / 1000;
/** An utterance is cut at 30 s (≤9 frames). */
export const CALL_AUDIO_FRAMES_MAX = 9;
export const CALL_UTTERANCE_MAX_MS = 30_000;
export const CALL_COMMAND_PAYLOADS = {
    'call.start': z.object({
        callId: CallId,
        mode: CallMode,
        speech: CallSpeechPath,
        /** The queue starts here (Home "Talk it through", or a push for one item). */
        focus: z.string().max(256).optional(),
    }),
    'call.utterance': z.object({
        callId: CallId,
        utteranceId: LocalId,
        text: z.string().min(1).max(4000),
        /** Handled on the device (fast path); the agent only learns what happened. */
        handled: z.string().max(400).optional(),
        interrupted: CallInterruption.optional(),
        /** The queue item on screen when the user spoke. */
        current: LocalId.optional(),
        /**
         * Actions this device signed on the call that went to another machine (a decision answered
         * where its run lives): recorded "via call" on the call's own record.
         */
        acted: z
            .array(z.object({
            id: z.string().min(1).max(256),
            kind: z.string().min(1).max(64),
            title: z.string().max(200),
            projectId: ProjectId.optional(),
        }))
            .max(10)
            .optional(),
    }),
    'call.audio': z.object({
        callId: CallId,
        utteranceId: LocalId,
        /** 0-based frame index within the utterance. */
        seq: z
            .number()
            .int()
            .nonnegative()
            .max(CALL_AUDIO_FRAMES_MAX - 1),
        /** base64 16 kHz mono Int16 little-endian PCM. */
        pcm: z
            .string()
            .max(Math.ceil((CALL_AUDIO_FRAME_MAX_BYTES * 4) / 3) + 4)
            .regex(/^[A-Za-z0-9+/]*={0,2}$/),
        last: z.boolean(),
        /** Dictation (not a call): transcribe only, never sent to an agent. */
        dictation: z.boolean().optional(),
    }),
    'call.end': z.object({ callId: CallId }),
    /**
     * Voice-tier ship to a non-production lane (staging, web preview, TestFlight). The daemon
     * refuses production lanes and any lane whose release gate needs a passkey; those go through
     * the secure batch as `land.deploy`.
     */
    'call.ship': z.object({
        callId: CallId,
        projectId: ProjectId,
        lane: z.string().min(1).max(80),
    }),
};
// ---------------------------------------------------------------------------------------------
// MCP tools for the call session
// ---------------------------------------------------------------------------------------------
export const CallShowInput = z.object({ target: CallShowTarget });
export const CallQueueSecureInput = z.object({
    kind: CallSecureKind,
    projectId: ProjectId,
    title: z.string().min(1).max(200),
    detail: z.string().max(400).optional(),
    /** land.merge: { workstreamId }; land.deploy: { projectId, lane }; decision: { decisionId, n }. */
    workstreamId: z.string().max(256).optional(),
    lane: z.string().max(80).optional(),
    decisionId: PrivateEntityId.optional(),
    n: z.number().int().min(1).max(9).optional(),
    note: z.string().max(4000).optional(),
});
export const CallNotifyWhenInput = z.object({
    condition: z.discriminatedUnion('type', [
        z.object({
            type: z.literal('build.ready'),
            projectId: ProjectId,
            lane: z.string().min(1).max(80),
        }),
        z.object({ type: z.literal('run.done'), runId: z.string().min(1).max(256) }),
        z.object({ type: z.literal('decision.new'), projectId: ProjectId }),
    ]),
    say: z.string().min(1).max(400),
});
export const FeedbackCaptureInput = z.object({
    projectId: ProjectId,
    source: CallFeedback.shape.source,
    /** Untrusted: quarantined, stored as data, never followed. */
    content: z.string().min(1).max(8000),
    taskId: z.string().max(256).optional(),
});
export const CallActInput = z.object({ action: CallVoiceAction });
/** Read one project's full brief section (no truncation) when the summary isn't enough. */
export const CallLookupInput = z.object({ projectId: ProjectId });
export const CallLookupOutput = z.object({ project: CallBriefProject.nullable() });
const Ack = z.object({ ok: z.boolean(), note: z.string().optional() });
export const CallQueueSecureOutput = z.object({
    id: z.string(),
    state: z.enum(['queued', 'refused']),
    /** Production items are offered for the passkey at once; the rest wait for the call's end. */
    offered: z.enum(['now', 'call-end']).optional(),
    reason: z.string().optional(),
});
export const FeedbackCaptureOutput = z.object({
    feedbackId: z.string(),
    projectId: z.string(),
    /** What was filed, for the agent to read back. */
    filed: z.string(),
});
/** MCP tools only a `role: 'call'` run may list or call (and the only tools it gets). */
export const CALL_MCP_TOOLS = {
    call_show: { input: CallShowInput, output: Ack },
    call_queue_secure: { input: CallQueueSecureInput, output: CallQueueSecureOutput },
    call_notify_when: { input: CallNotifyWhenInput, output: Ack },
    feedback_capture: { input: FeedbackCaptureInput, output: FeedbackCaptureOutput },
    call_act: { input: CallActInput, output: Ack },
    call_lookup: { input: CallLookupInput, output: CallLookupOutput },
};
export const CALL_ONLY_TOOLS = Object.keys(CALL_MCP_TOOLS);
const NUM = {
    one: 1,
    first: 1,
    '1': 1,
    '1st': 1,
    two: 2,
    second: 2,
    '2': 2,
    '2nd': 2,
    to: 2,
    too: 2,
    three: 3,
    third: 3,
    '3': 3,
    '3rd': 3,
    four: 4,
    fourth: 4,
    '4': 4,
    '4th': 4,
    for: 4,
    five: 5,
    fifth: 5,
    '5': 5,
    '5th': 5,
    six: 6,
    sixth: 6,
    '6': 6,
    seven: 7,
    seventh: 7,
    '7': 7,
    eight: 8,
    eighth: 8,
    '8': 8,
    nine: 9,
    ninth: 9,
    '9': 9,
};
const normFast = (s) => s
    .toLowerCase()
    .replace(/[’`]/g, "'")
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
/**
 * The fast-path grammar: "next", "skip", "repeat", "stop", "approve", and option numbers
 * ("two", "the second one", "option 3", "number two"), optionally followed by a short note
 * ("the third, but warmer"). Anything else goes to the agent. `options` is the current item's
 * option count: a number beyond it is not a pick. The homophones "to"/"too"/"for" only count
 * as numbers on their own.
 */
export function parseCallFastPath(raw, options) {
    const t = normFast(raw);
    if (!t)
        return undefined;
    if (/^(next|next one|go on|move on|continue)$/.test(t))
        return { kind: 'next' };
    if (/^(skip|skip (it|this|this one)|later|not now|pass)$/.test(t))
        return { kind: 'skip' };
    if (/^(repeat|repeat that|say (that|it) again|again|come again|pardon)$/.test(t))
        return { kind: 'repeat' };
    if (/^(stop|stop talking|quiet|be quiet|hold on|wait|shush)$/.test(t))
        return { kind: 'stop' };
    if (/^(approve|approve (it|them|all|those)|yes approve)$/.test(t))
        return { kind: 'approve' };
    const m = /^(?:(?:let'?s )?(?:go with |pick |take |choose )?(?:the )?(?:option |number )?(?<w>[a-z0-9]+)(?: one| option)?)(?:(?:,| but| and)\s*(?<note>.+))?$/.exec(t);
    if (!m?.groups)
        return undefined;
    const w = m.groups.w;
    const n = NUM[w];
    if (n === undefined || n > options)
        return undefined;
    const note = m.groups.note?.trim();
    if (['to', 'too', 'for'].includes(w) && (note || t !== w))
        return undefined;
    if (note) {
        // Keep the note's own wording from the raw text.
        const lower = raw.toLowerCase();
        const at = lower.lastIndexOf(note.split(' ')[0]);
        const original = at >= 0
            ? raw
                .slice(at)
                .replace(/[.!?]+$/, '')
                .trim()
            : note;
        return { kind: 'pick', n, note: original };
    }
    return { kind: 'pick', n };
}
/**
 * Untrusted content (TestFlight, share sheet, screenshots' OCR, spoken feedback) is wrapped as
 * quarantined data before any agent sees it. The wrapper neutralises a forged closing tag.
 */
export function wrapUntrusted(source, content) {
    const safeSource = source.replace(/[^a-z0-9_-]/gi, '').slice(0, 32) || 'unknown';
    const body = content.replace(/<\s*\/?\s*untrusted[^>]*>/gi, '[tag removed]');
    return `<untrusted source="${safeSource}">\n${body}\n</untrusted>`;
}
// ---------------------------------------------------------------------------------------------
// Status answers from the brief alone (device fast path + the VC3 eval set)
// ---------------------------------------------------------------------------------------------
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const end = (s) => (/[.!?]$/.test(s.trim()) ? s.trim() : `${s.trim()}.`);
/**
 * A decision read aloud: the question, then each stored option label verbatim, numbered in
 * stored order, then the suggested one. Never paraphrased.
 */
export function spokenDecision(projectName, d) {
    const opts = d.options.map((o) => `${o.n}: ${end(o.label)}`).join(' ');
    const sug = d.options.find((o) => o.suggested);
    return [
        `${projectName}: ${end(d.question)}`,
        opts,
        sug ? `Suggested: ${sug.n}.` : '',
        d.secure ? 'This one needs your passkey.' : '',
    ]
        .filter(Boolean)
        .join(' ');
}
/** Which status question a phrase asks, if any (the rest goes to the agent). */
export function statusQuestion(raw) {
    const t = normFast(raw);
    const about = /\b(?:in|for|on|with) (?:the )?(?<p>[\p{L}\p{N} ]+?)(?: project)?$/u.exec(t)?.groups
        ?.p;
    const project = about ? { project: about } : {};
    if (/^(what needs me|what do you need from me|what'?s waiting on me|what needs my attention)\b/.test(t))
        return { q: 'needs', ...project };
    if (/^(what'?s|what is|anything) (blocked|stuck)\b/.test(t))
        return { q: 'blocked', ...project };
    if (/^(what'?s|what is) (running|in progress|being worked on)\b/.test(t))
        return { q: 'running', ...project };
    if (/^(what'?s|what is) (live|shipped|deployed)\b|^(any )?builds?\b/.test(t))
        return { q: 'builds', ...project };
    if (/^(any |what'?s the )?(new )?feedback\b/.test(t))
        return { q: 'feedback', ...project };
    return undefined;
}
/** Answer a status question from the brief alone (exact numbers; options verbatim). */
export function answerFromBrief(brief, q, project) {
    const scope = project
        ? brief.projects.filter((p) => normFast(p.name) === normFast(project))
        : brief.projects;
    if (project && !scope.length)
        return `I have nothing on ${project}.`;
    switch (q) {
        case 'needs': {
            const total = project ? scope.reduce((n, p) => n + p.needs, 0) : brief.needsTotal;
            if (!total)
                return 'Nothing needs you right now.';
            const top = scope.find((p) => p.decisions.length);
            const head = `${total} ${total === 1 ? 'needs' : 'need'} you.`;
            const by = scope
                .filter((p) => p.needs)
                .map((p) => `${p.name} ${p.needs}`)
                .join(', ');
            return [
                head,
                by ? `${by}.` : '',
                top ? `First, ${spokenDecision(top.name, top.decisions[0])}` : '',
            ]
                .filter(Boolean)
                .join(' ');
        }
        case 'blocked': {
            const rows = scope.flatMap((p) => p.blockers.map((b) => `${p.name}: ${b.title}, ${b.reason}`));
            return rows.length
                ? `${plural(rows.length, 'blocker')}. ${rows.map(end).join(' ')}`
                : 'Nothing is blocked.';
        }
        case 'running': {
            const rows = scope.flatMap((p) => p.running
                .filter((r) => r.status === 'running')
                .map((r) => `${p.name}: ${r.title}${r.step ? `, ${r.step}` : ''}`));
            return rows.length
                ? `${rows.length} running. ${rows.map(end).join(' ')}`
                : 'Nothing is running.';
        }
        case 'builds': {
            const rows = scope.flatMap((p) => p.builds.map((b) => `${p.name}: ${b.label}${b.version ? ` ${b.version}` : ''} on ${b.lane}, ${b.status}`));
            return rows.length ? rows.map(end).join(' ') : 'No recent builds.';
        }
        case 'feedback': {
            const rows = scope.flatMap((p) => p.feedback.map((f) => `${p.name}, from ${f.source}`));
            return rows.length
                ? `${plural(rows.length, 'new feedback item')}: ${rows.join('; ')}. They're on screen.`
                : 'No new feedback.';
        }
    }
}
//# sourceMappingURL=call.js.map